'use client';

import { useState, useEffect } from 'react';

interface ScrollProgressIndicatorProps {
    showMilestones?: boolean;
}

const milestones = [
    { percentage: 20, label: 'Discovering', icon: '✨' },
    { percentage: 40, label: 'Learning', icon: '🔮' },
    { percentage: 60, label: 'Believing', icon: '⭐' },
    { percentage: 80, label: 'Deciding', icon: '🌙' },
    { percentage: 100, label: 'Joining', icon: '🚀' },
];

const ScrollProgressIndicator = ({ showMilestones = true }: ScrollProgressIndicatorProps) => {
    const [scrollProgress, setScrollProgress] = useState<number>(0);
    const [activeMilestone, setActiveMilestone] = useState<number>(-1);
    const [showCelebration, setShowCelebration] = useState<boolean>(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const progress = Math.min((scrollTop / docHeight) * 100, 100);
            setScrollProgress(progress);

            if (showMilestones) {
                const currentMilestone = milestones.findIndex(
                    (m, index) =>
                        progress >= m.percentage &&
                        (index === milestones.length - 1 || progress < milestones[index + 1].percentage)
                );

                if (currentMilestone !== activeMilestone && currentMilestone !== -1) {
                    setActiveMilestone(currentMilestone);
                    setShowCelebration(true);
                    setTimeout(() => setShowCelebration(false), 2000);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeMilestone, showMilestones]);

    return (
        <div className="fixed top-[60px] md:top-[80px] left-0 right-0 z-[999]">
            <div className="relative h-1 bg-muted/30">
                <div
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary via-secondary to-accent transition-all duration-300 ease-out"
                    style={{ width: `${scrollProgress}%` }}
                >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-accent shadow-glow-accent animate-pulse" />
                </div>

                {showMilestones &&
                    milestones.map((milestone, index) => (
                        <div
                            key={milestone.percentage}
                            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-300"
                            style={{ left: `${milestone.percentage}%` }}
                        >
                            <div
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${scrollProgress >= milestone.percentage
                                        ? 'bg-accent scale-150 shadow-glow-accent'
                                        : 'bg-muted-foreground/30 scale-100'
                                    }`}
                            />
                        </div>
                    ))}
            </div>

            {showCelebration && activeMilestone !== -1 && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 animate-in fade-in slide-in-from-top-2 duration-500">
                    <div className="bg-card/95 backdrop-blur-sm px-6 py-3 rounded-full shadow-card-depth border border-primary/30">
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl animate-float">{milestones[activeMilestone].icon}</span>
                            <div>
                                <p className="text-xs text-muted-foreground font-medium">Journey Progress</p>
                                <p className="text-sm font-semibold text-foreground font-playfair">
                                    {milestones[activeMilestone].label}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScrollProgressIndicator;